import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { MIN_POLL_MS, MAX_ATTEMPTS, TICK_MS, clamp, nextInterval, secondsLeft } from './deviceCodePolling';

import { getMigrationById } from '../api/migrations';
import {
  ActivateWaveResult,
  MintSession,
  activateWave,
  beginMintSession,
  waveErrorCode,
  waveRetryAfterSeconds,
} from '../api/waves';

// FD-304 (FD-ADR-032) — the guided wave-activation state machine.
//
// Split out of the dialog for exactly RouteLoader/useRouteHold's reason: the
// timing policy is deterministic and unit-testable on fake timers, without
// fighting a Fluent dialog harness or framer-motion.
//
// ── THE POLL IS A SETTLE-THEN-SCHEDULE CHAIN, NOT AN INTERVAL ───────────────
//
// This is the decision with the most consequence in the whole epic, and it is
// structural rather than a coding convention.
//
// Every poll is a real `POST activate-wave`. While the operator is still
// signing in it returns 409 AUTHORIZATION_PENDING almost immediately — but the
// FIRST attempt after they finish does the entire mint inside that one request:
// `Invoke-BprtPoll` alone runs up to ~62s before the Key Vault write, the table
// write and the registry update. A `setInterval` firing during that window
// would issue a SECOND activate-wave while the first is still minting. That
// mints two `package_<pid>` users; the second overwrites `bprt-<migrationId>`
// and re-stamps the row, leaving the first BPRT **live and orphaned with no
// `BprtPackagePid` recorded to revoke it** — the FD-185 silent-kill-switch
// class of defect, arriving from the client.
//
// Because the next timeout is only ever armed from the settle handler, "at most
// one activation in flight" is a property of the shape, not of care. The
// regression test holds `activateWave` unresolved and advances timers well past
// several intervals; the call count must still be 1.
//
// `useQuery` + `refetchInterval` is separately wrong: every pending poll is a
// 409 that `postJsonWithStatus` throws, and refetch-on-focus / on-reconnect
// would each fire an extra ACTIVATION POST — a background tab regaining focus
// must not be able to mint.

// FD-530 — the constants and the two pure functions moved to
// `deviceCodePolling.ts` when the tenant-application flows became the second and
// third consumers of this policy. Re-exported so every existing importer (and
// this hook's own timer tests) is untouched; the behaviour is byte-identical.
export { MIN_POLL_MS, MAX_POLL_MS, MAX_ATTEMPTS, TICK_MS } from './deviceCodePolling';

/**
 * How long an in-flight attempt must run before we call it a mint rather than a
 * pending check. A pending 409 is one token POST and returns fast; the
 * successful attempt performs the whole mint and can take a minute. Crossing
 * this line is the only signal available that the sign-in landed, and without
 * it the dialog looks stalled for 60s at the exact moment it is doing the work.
 */
export const ACTIVATING_HINT_MS = 10_000;

export type ActivationPhase =
  | { kind: 'idle' }
  /** `mint-session` in flight. */
  | { kind: 'starting' }
  /** The operator has the code and has not finished signing in. */
  | { kind: 'awaiting-signin'; session: MintSession; secondsRemaining: number }
  /** An attempt has been running long enough that it is minting, not polling. */
  | { kind: 'activating'; session: MintSession }
  | { kind: 'done'; result: ActivateWaveResult }
  /** The 15-minute window closed. Nothing was minted; nothing to clean up. */
  | { kind: 'expired' }
  | { kind: 'failed'; error: unknown };

export interface DeviceCodeActivation {
  phase: ActivationPhase;
  /** Begins a session. Called from a click, never an effect. */
  start: (expiryDays: number) => void;
  /** Abandons the session. Makes NO server call — see below. */
  cancel: () => void;
}

export function useDeviceCodeActivation(
  migrationId: string,
  onDone?: () => void,
): DeviceCodeActivation {
  const [phase, setPhase] = useState<ActivationPhase>({ kind: 'idle' });
  const queryClient = useQueryClient();

  const pollTimer = useRef<number | undefined>(undefined);
  const tickTimer = useRef<number | undefined>(undefined);
  const intervalMs = useRef(MIN_POLL_MS);
  const attempts = useRef(0);
  const deadlineMs = useRef(0);
  const running = useRef(false);
  const inFlightSince = useRef(0);

  // onDone is called from a timer callback that outlives the render it was
  // armed in, so it is read through a ref rather than captured — otherwise a
  // parent re-render mid-poll would invoke a stale closure.
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;

  const stop = useCallback(() => {
    running.current = false;
    if (pollTimer.current !== undefined) window.clearTimeout(pollTimer.current);
    if (tickTimer.current !== undefined) window.clearInterval(tickTimer.current);
    pollTimer.current = undefined;
    tickTimer.current = undefined;
    inFlightSince.current = 0;
  }, []);

  // Unmount stops the chain. Without this a closed dialog keeps activating the
  // wave in the background — the poll is a mutation, not a read.
  useEffect(() => stop, [stop]);

  /**
   * A terminal device-code error does NOT immediately mean failure. If an
   * attempt succeeded server-side but its response was lost — entirely possible
   * on a request that can run a minute — the retry gets `invalid_grant` and we
   * would report failure for a wave that IS active. One authoritative read
   * removes the only way this flow can lie.
   */
  const reconcile = useCallback(async (): Promise<ActivateWaveResult | null> => {
    try {
      const detail = await queryClient.fetchQuery({
        queryKey: ['migration', migrationId],
        queryFn: () => getMigrationById(migrationId),
        staleTime: 0,
      });
      const m = detail.migration;
      if ((m.bprtStatus ?? '').toLowerCase() === 'active') {
        return {
          migrationId,
          bprtStatus: m.bprtStatus as string,
          expiresUtc: m.bprtExpiresUtc ?? '',
          // Derived from the row, never assumed — a fabricated `true` here
          // would be the console asserting a directory object exists.
          packageUserPresent: Boolean(m.bprtPackagePid),
        };
      }
    } catch {
      // Best-effort. A failed re-check must not mask the original error.
    }
    return null;
  }, [migrationId, queryClient]);

  const succeed = useCallback(
    (result: ActivateWaveResult) => {
      stop();
      setPhase({ kind: 'done', result });
      void queryClient.invalidateQueries({ queryKey: ['migration', migrationId] });
      onDoneRef.current?.();
    },
    [migrationId, queryClient, stop],
  );

  const poll = useCallback(
    async (session: MintSession, expiryDays: number) => {
      if (!running.current) return;

      // The deadline is Microsoft's, and there is nothing to tell the Manager
      // when it passes — so expiry stops with ZERO further calls.
      if (Date.now() >= deadlineMs.current) {
        stop();
        setPhase({ kind: 'expired' });
        return;
      }
      if (attempts.current >= MAX_ATTEMPTS) {
        stop();
        setPhase({ kind: 'expired' });
        return;
      }

      attempts.current += 1;
      inFlightSince.current = Date.now();

      try {
        const result = await activateWave(migrationId, { deviceCode: session.deviceCode, expiryDays });
        if (!running.current) return;
        succeed(result);
        return;
      } catch (err) {
        if (!running.current) return;
        inFlightSince.current = 0;

        if (waveErrorCode(err) === 'AUTHORIZATION_PENDING') {
          intervalMs.current = nextInterval(intervalMs.current, waveRetryAfterSeconds(err));
          setPhase({
            kind: 'awaiting-signin',
            session,
            secondsRemaining: secondsLeft(deadlineMs.current),
          });
          pollTimer.current = window.setTimeout(
            () => void poll(session, expiryDays),
            intervalMs.current,
          );
          return;
        }

        const recovered = await reconcile();
        if (!running.current) return;
        if (recovered) {
          succeed(recovered);
          return;
        }
        stop();
        setPhase({ kind: 'failed', error: err });
      }
    },
    [migrationId, reconcile, stop, succeed],
  );

  const start = useCallback(
    (expiryDays: number) => {
      // Idempotent against a double click and against StrictMode — `start` is
      // only ever called from a click handler, never from an effect.
      if (running.current) return;
      running.current = true;
      attempts.current = 0;
      intervalMs.current = MIN_POLL_MS;
      setPhase({ kind: 'starting' });

      void (async () => {
        let session: MintSession;
        try {
          session = await beginMintSession(migrationId);
        } catch (err) {
          if (!running.current) return;
          stop();
          setPhase({ kind: 'failed', error: err });
          return;
        }
        if (!running.current) return;

        deadlineMs.current = Date.parse(session.expiresUtc);
        if (!Number.isFinite(deadlineMs.current)) {
          // A malformed expiry must not become an unbounded poll. Fall back to
          // the relative window the same response carries.
          deadlineMs.current = Date.now() + Math.max(0, session.expiresInSeconds) * 1_000;
        }
        intervalMs.current = clamp(session.intervalSeconds * 1_000);

        setPhase({
          kind: 'awaiting-signin',
          session,
          secondsRemaining: secondsLeft(deadlineMs.current),
        });

        tickTimer.current = window.setInterval(() => {
          const remaining = secondsLeft(deadlineMs.current);
          setPhase((current) => {
            if (current.kind === 'awaiting-signin') {
              // Promote to `activating` once an attempt has outrun a pending
              // check — the response has not arrived, so this is the mint.
              if (inFlightSince.current > 0 && Date.now() - inFlightSince.current >= ACTIVATING_HINT_MS) {
                return { kind: 'activating', session: current.session };
              }
              return { ...current, secondsRemaining: remaining };
            }
            return current;
          });
          // Expiry is enforced here as well as in `poll`, because the gap
          // between two polls is up to 60s and the countdown must not sit at
          // zero while a stale interval waits to fire.
          if (remaining <= 0 && inFlightSince.current === 0) {
            stop();
            setPhase({ kind: 'expired' });
          }
        }, TICK_MS);

        pollTimer.current = window.setTimeout(
          () => void poll(session, expiryDays),
          intervalMs.current,
        );
      })();
    },
    [migrationId, poll, stop],
  );

  const cancel = useCallback(() => {
    // NO server call. A device code has no revoke — it simply expires. Anything
    // we sent here would be inventing an endpoint to make the UI feel tidy.
    stop();
    setPhase({ kind: 'idle' });
  }, [stop]);

  return { phase, start, cancel };
}


